import { useState, useEffect, useRef, useMemo } from 'react';
import cronstrue from 'cronstrue';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface SyncScheduleSelectProps {
  value: string;
  onChange: (cronExpression: string) => void;
  scheduleEnabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const SCHEDULE_OPTIONS = [
  { label: 'Once a day (12am)', value: '0 0 * * *' },
  { label: 'Once a week (12am Sun)', value: '0 0 * * 0' },
  { label: 'Once a month (12am 1st)', value: '0 0 1 * *' },
];

const CUSTOM_SENTINEL = '__custom__';

function isPresetValue(val: string) {
  return SCHEDULE_OPTIONS.some((opt) => opt.value === val);
}

function isTooFrequent(expr: string): boolean {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  const [minute] = parts;
  // If the minute field contains a step or comma (e.g. */5, 0,30), it runs more than once per hour
  if (minute.includes('/') || minute.includes(',')) {
    // Exception: */60 or a single step that results in once-per-hour is fine,
    // but simplest check: any step divisor < 60 means sub-hourly
    const stepMatch = minute.match(/\*\/(\d+)/);
    if (stepMatch && parseInt(stepMatch[1], 10) >= 60) return false;
    return true;
  }
  // Wildcard minute = every minute
  if (minute === '*') return true;
  // Range like 0-30 = runs every minute in that range
  if (minute.includes('-')) return true;
  return false;
}

function validateCron(expr: string): { valid: boolean; description?: string; error?: string } {
  if (!expr.trim()) {
    return { valid: false };
  }
  try {
    const description = cronstrue.toString(expr);
    if (isTooFrequent(expr)) {
      return { valid: false, error: 'Schedule cannot run more frequently than once per hour' };
    }
    return { valid: true, description };
  } catch {
    return { valid: false, error: 'Invalid cron expression' };
  }
}

export function SyncScheduleSelect({
  value,
  onChange,
  scheduleEnabled,
  onToggle,
  disabled,
}: SyncScheduleSelectProps) {
  const [isCustomMode, setIsCustomMode] = useState(() => !isPresetValue(value) && !!value);
  const [customInput, setCustomInput] = useState(() => (!isPresetValue(value) ? value : ''));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when external value changes (e.g., API load)
  useEffect(() => {
    if (isPresetValue(value)) {
      setIsCustomMode(false);
    } else if (value) {
      setIsCustomMode(true);
      setCustomInput(value);
    }
  }, [value]);

  const validation = useMemo(() => {
    if (!isCustomMode) return null;
    return validateCron(customInput);
  }, [isCustomMode, customInput]);

  // Propagate valid custom expressions to parent
  useEffect(() => {
    if (isCustomMode && validation?.valid && customInput !== value) {
      onChange(customInput);
    }
  }, [isCustomMode, validation, customInput, value, onChange]);

  const handleSelectChange = (newValue: string) => {
    if (newValue === CUSTOM_SENTINEL) {
      setIsCustomMode(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setIsCustomMode(false);
      setCustomInput('');
      onChange(newValue);
    }
  };

  const selectValue = isCustomMode ? CUSTOM_SENTINEL : value;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Sync Schedule</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {scheduleEnabled ? 'Enabled' : 'Disabled'}
          </span>
          <Switch
            checked={scheduleEnabled}
            onCheckedChange={onToggle}
            disabled={disabled}
          />
        </div>
      </div>
      <Select value={selectValue} onValueChange={handleSelectChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder="Select schedule...">
            {isCustomMode
              ? 'Custom'
              : SCHEDULE_OPTIONS.find((o) => o.value === value)?.label}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {SCHEDULE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={CUSTOM_SENTINEL}>Custom...</SelectItem>
        </SelectContent>
      </Select>
      {isCustomMode && (
        <div className="space-y-1.5">
          <Input
            ref={inputRef}
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            placeholder="e.g. 0 */6 * * *"
            disabled={disabled}
            className={validation && !validation.valid ? 'border-destructive' : ''}
          />
          {validation?.valid && validation.description && (
            <p className="text-xs text-muted-foreground">{validation.description}</p>
          )}
          {validation && !validation.valid && validation.error && (
            <p className="text-xs text-destructive">{validation.error}</p>
          )}
          {!customInput.trim() && (
            <p className="text-xs text-muted-foreground">
              minute hour day month weekday
            </p>
          )}
        </div>
      )}
    </div>
  );
}
